<div class="split">
  <section class="panel list-panel" aria-labelledby="findings-heading">
    <div class="section-heading">
      <div>
        <p class="eyebrow">DETERMINISTIC RULES</p>
        <h2 id="findings-heading">Findings</h2>
      </div>
      <span class="quiet-status"
        ><mat-icon>verified</mat-icon>{{ normalCount() }} normal events</span
      >
    </div>

    <div class="toolbar">
      <div class="chips" role="group" aria-label="Filter by severity">
        @for (filter of filters; track filter.value) {
          <button
            type="button"
            class="chip"
            [class.active]="severity() === filter.value"
            [attr.aria-pressed]="severity() === filter.value"
            (click)="severity.set(filter.value)"
          >
            {{ filter.label }}
            <span class="chip-count">{{ counts()[filter.value] }}</span>
          </button>
        }
      </div>
      <label class="search">
        <mat-icon aria-hidden="true">search</mat-icon>
        <input
          type="search"
          placeholder="Search WRP or rule"
          aria-label="Search findings"
          [value]="query()"
          (input)="onQuery($event)"
        />
      </label>
    </div>

    @if (!visible().length) {
      <div class="empty-state">
        <mat-icon>check_circle</mat-icon>
        @if (findings().length) {
          <h3>No findings match the filter</h3>
          <p>Clear the search or choose another severity.</p>
        } @else {
          <h3>No findings in this snapshot</h3>
          <p>The replayed log and the register matched every rule.</p>
        }
      </div>
    } @else {
      <ul class="rows" role="list">
        @for (finding of visible(); track finding.id) {
          <li>
            <button
              type="button"
              class="row"
              [class.selected]="selected()?.id === finding.id"
              [attr.aria-current]="selected()?.id === finding.id"
              (click)="selectedId.set(finding.id)"
            >
              <span
                class="severity"
                [class.high]="finding.severity === 'high'"
                [class.warning]="finding.severity === 'warning'"
                [class.info]="finding.severity === 'info'"
                ><mat-icon aria-hidden="true">{{
                  finding.severity === "high"
                    ? "priority_high"
                    : finding.severity === "info"
                      ? "info"
                      : "visibility"
                }}</mat-icon
                ><span class="sr-only">{{ finding.severity }}</span></span
              >
              <span class="row-copy"
                ><strong>{{ finding.title }}</strong
                ><small
                  >{{ name(finding.wrpId) }} ·
                  {{ finding.observedAt | date: "medium" }}</small
                ></span
              >
              <span class="rule-tag">{{ finding.rule }}</span>
              <mat-icon aria-hidden="true">chevron_right</mat-icon>
            </button>
          </li>
        }
      </ul>
    }

    <details class="rule-catalogue">
      <summary>How findings are produced</summary>
      <dl>
        @for (rule of rules; track rule.rule) {
          <dt>
            <span class="mono">{{ rule.rule }}</span> {{ rule.title }}
            <em>{{ rule.severity }}</em>
          </dt>
          <dd>{{ rule.description }}</dd>
        }
      </dl>
    </details>
  </section>

  <aside class="panel detail-panel" aria-live="polite">
    @if (selected(); as finding) {
      <p class="eyebrow">FINDING EVIDENCE</p>
      <div
        class="detail-rule"
        [class.high]="finding.severity === 'high'"
        [class.warning]="finding.severity === 'warning'"
        [class.info]="finding.severity === 'info'"
      >
        {{ finding.rule }} · {{ finding.severity }}
      </div>
      <h2>{{ finding.title }}</h2>
      @if (finding.wrpId) {
        <button
          type="button"
          class="link-button"
          (click)="openWrp.emit(finding.wrpId)"
        >
          {{ name(finding.wrpId) }}
          <mat-icon aria-hidden="true">arrow_forward</mat-icon>
        </button>
      } @else {
        <p class="detail-sub">{{ name(finding.wrpId) }}</p>
      }
      <dl class="facts">
        <dt>Observed</dt>
        <dd>{{ finding.observedAt | date: "medium" }}</dd>
        <dt>Rule result</dt>
        <dd>{{ finding.detail }}</dd>
        @if (finding.wrpId) {
          <dt>Entity</dt>
          <dd class="mono">
            <a [href]="wrpLink(finding.wrpId)" target="_blank" rel="noopener">{{
              finding.wrpId
            }}</a>
          </dd>
        }
        @if (finding.source; as source) {
          <dt>Source</dt>
          <dd class="mono">
            <a [href]="source" target="_blank" rel="noopener">{{ source }}</a>
          </dd>
        }
      </dl>
      <h3>Evidence events</h3>
      <ul class="evidence-list" role="list">
        @for (event of finding.evidence; track $index) {
          @let info = describe(event);
          <li>
            <a [href]="eventLink(event)" target="_blank" rel="noopener">
              <span class="mono seq">#{{ event.sequence_number }}</span>
              <span class="evidence-copy">
                <strong>{{ info.label }} {{ info.action }}</strong>
                <small>
                  {{ event.published_at | date: "medium" }}
                  @if (info.subject) {
                    · {{ info.subject }}
                  }
                </small>
                @if (info.reason) {
                  <small>Reason: {{ info.reason }}</small>
                }
                @if (info.status) {
                  <small>Status: {{ info.status }}</small>
                }
                @if (info.fingerprint) {
                  <small class="mono">{{ info.fingerprint }}</small>
                }
              </span>
              <mat-icon aria-hidden="true">open_in_new</mat-icon>
            </a>
            @if (info.record; as record) {
              <button
                type="button"
                class="icon-button"
                title="Decoded certificate"
                aria-label="Decoded certificate"
                (click)="inspect(record)"
              >
                <mat-icon aria-hidden="true">article</mat-icon>
              </button>
            }
          </li>
        } @empty {
          <li class="muted">
            This finding comes from
            {{
              finding.source ? "the API response above" : "the register API"
            }}, not from a log event.
          </li>
        }
      </ul>
      <p class="evidence-note">
        <mat-icon aria-hidden="true">lock_open</mat-icon>Evidence links to the
        public Registrar API. No privileged data is used.
      </p>
    } @else {
      <div class="empty-state compact">
        <mat-icon>fact_check</mat-icon>
        <p>Select a finding to inspect its evidence.</p>
      </div>
    }
  </aside>
</div>
